<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <div class="wizard-page-wrapper flex">
    <span class="widget-page absolute top-0 start-0 mt-32 ms-40 text-gray">
      {{ index }}/{{ totalPages - 1 }}
    </span>
    <div class="wizard-page-content flex-col px-40 pt-40">
      <h1 class="text-3xl font-bolt">{{ title }}</h1>
      <p v-if="description">{{ description }}&nbsp;<span v-if="showRequiredLegend">{{ requiredLegend }}</span></p>
      <slot name="content"></slot>
      <div class="widget-buttons border-top flex justify-end pt-16">
        <BaseButton v-if="index > 0" class="btn btn-default" @click="$emit('prev')" >
          {{ backString }}
        </BaseButton>
        <BaseButton v-if="index !== totalPages - 1" class="btn btn-primary" :disabled="nextDisabled || !canProceed" @click="$emit('next')">
          {{ nextString }}
        </BaseButton>
        <BaseButton v-else class="btn btn-primary" @click="$emit('save')">
          {{ doneString }}
        </BaseButton>
      </div>
    </div>
    <div class="wizard-side-illustration">
      <slot name="illustration"></slot>
    </div>
    <IconButton
      class="btn btn-link absolute top-0 end-0 p-16"
      icon="bi/x-lg.svg"
      iconClass="bi-18"
      :title="closeString"
      :aria-label="closeString"
      @click="$emit('abort')"
    />
  </div>
</template>

<script setup>
import gt from 'gettext'
import BaseButton from '@/io.ox/vueComponents/base-button.vue'
import IconButton from '@/io.ox/vueComponents/icon-button.vue'

defineProps({
  title: String,
  description: String,
  index: Number,
  totalPages: Number,
  nextDisabled: Boolean,
  showRequiredLegend: Boolean,
  canProceed: {
    type: Boolean,
    default: true
  }
})

defineEmits(['prev', 'next', 'save', 'abort'])

const requiredLegend = gt('Fields marked with * are required.')

const backString = gt('Back')
const nextString = gt('Next')
const doneString = gt('Done')
const closeString = gt('Close')
</script>

<style scoped lang="scss">
$content-height: 510px;

.wizard-page-wrapper {
  container: wrapper / inline-size;
  .wizard-page-content {
    width: 500px;
    max-width: 100vw;
    height: $content-height;

    @container wrapper ( width < 765px) {
      width: 100%;
    }
  }

  .wizard-side-illustration {
    width: 40%;
    height: $content-height;
    background: var(--accent-a10);

    @container wrapper ( width < 765px) {
      display: none;
    }
  }
}

.widget-buttons {
  margin: auto -40px 16px;

  button {
    min-width: 86px;
    margin-inline-end: 16px;
  }
}
</style>
